import { useState, useEffect, useCallback } from "@wordpress/element";

const STORAGE_KEY = "wpbean_pgs_shortcuts";

export const DEFAULT_SHORTCUTS = {
    save: { key: "s", ctrl: true, shift: false, alt: false, label: "Save" },
    undo: { key: "z", ctrl: true, shift: false, alt: false, label: "Undo" },
    redo: { key: "z", ctrl: true, shift: true, alt: false, label: "Redo" },
    search: {
        key: "k",
        ctrl: true,
        shift: false,
        alt: false,
        label: "Open Search",
    },
};

function load() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return DEFAULT_SHORTCUTS;
        const saved = JSON.parse(raw);
        // Merge with defaults so new shortcuts added in future updates appear
        return { ...DEFAULT_SHORTCUTS, ...saved };
    } catch {
        return DEFAULT_SHORTCUTS;
    }
}

/** Returns true when the event matches a shortcut descriptor. */
export function matchesShortcut(e, shortcut) {
    const ctrlOrMeta = e.ctrlKey || e.metaKey;
    return (
        e.key.toLowerCase() === shortcut.key.toLowerCase() &&
        !!shortcut.ctrl === ctrlOrMeta &&
        !!shortcut.shift === e.shiftKey &&
        !!shortcut.alt === e.altKey
    );
}

const isMac =
    typeof navigator !== "undefined" && /mac/i.test(navigator.platform);

/** Produce a human-readable label like ⌘K or Ctrl+K. */
export function formatShortcut(sc) {
    if (!sc?.key) return "";
    const parts = [];
    if (sc.ctrl) parts.push(isMac ? "⌘" : "Ctrl");
    if (sc.alt) parts.push(isMac ? "⌥" : "Alt");
    if (sc.shift) parts.push(isMac ? "⇧" : "Shift");
    parts.push(sc.key.toUpperCase());
    return isMac ? parts.join("") : parts.join("+");
}

export function useKeyboardShortcuts() {
    const [shortcuts, setShortcuts] = useState(load);

    const update = useCallback((id, sc) => {
        setShortcuts((prev) => {
            const next = { ...prev, [id]: { ...prev[id], ...sc } };
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
            } catch {}
            return next;
        });
    }, []);

    const reset = useCallback(() => {
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch {}
        setShortcuts(DEFAULT_SHORTCUTS);
    }, []);

    return { shortcuts, update, reset };
}
